import { Trans, withI18n } from '@lingui/react'
import classNames from 'classnames'
import { arrayOf, bool, number, shape, string } from 'prop-types'

import IconSendEnvelope from 'views/components/atoms/Icon/IconSendEnvelope'
import Image from 'views/components/atoms/Image'
import Price from 'views/components/atoms/Price'
import {
  currencyToSymbol,
  formatPriceString,
} from 'views/components/atoms/Price/helpers'
import WrapIfNeeded from 'views/components/atoms/WrapIfNeeded'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import TransWithHtml from 'views/components/utils/TransWithHtml'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { publicRuntimeConfigType } from 'views/utils/propTypes'

import { resolveImages } from './helpers'

const DisplayShippingData = withI18n()(
  ({ i18n, shippingData, deliveryCosts, currency }) => {
    // rows of the table
    let rows = []

    // header
    let headerRow = []

    // decide which data to take
    if (shippingData?.carriers.length) {
      const { carriers } = shippingData

      // prepare data
      const shippingCostThreshold = carriers[0]?.freeShippingThreshold

      const minAmountPrice = formatPriceString({
        amount: shippingCostThreshold,
        locale: i18n.language,
        currencySymbol: currencyToSymbol(currency),
        symbolRight: true,
      })

      headerRow = [
        {
          text: i18n._('buyBox.delivery.description.serviceProvider'),
          hidden: true,
        },
        {
          text: i18n._('buyBox.delivery.below.price.label', {
            minAmount: minAmountPrice,
          }),
        },
        {
          text: i18n._('buyBox.delivery.from.price.label', {
            minAmount: minAmountPrice,
          }),
        },
      ]

      rows.push(headerRow)

      // enrich data
      for (const carrierData of carriers) {
        const currentTableDataItem = [
          {
            logo: `LOGO_${carrierData.group}`,
            alt: carrierData.group,
          },
          {
            price: carrierData.shippingCost,
          },
          {
            text: i18n._('buyBox.delivery.noCosts.text'),
            bold: true,
            color: 'positive',
          },
        ]

        rows.push(currentTableDataItem)
      }

      // map logos
      deliveryCosts.rows = rows

      const deliveryCostsConfig = resolveImages(deliveryCosts)
      rows = deliveryCostsConfig.rows
    } else {
      const deliveryCostsConfig = resolveImages(deliveryCosts)
      rows = deliveryCostsConfig.rows
    }

    return DeliveryTable(rows, currency)
  }
)

const DeliveryTable = (rows, currency) => {
  const headerRow = rows.shift()

  return (
    <table className="a-table" cellSpacing="0">
      <thead>
        <DeliveryTableRow row={headerRow} currency={currency} head />
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <DeliveryTableRow
            key={row?.alt || index}
            row={row}
            currency={currency}
          />
        ))}
      </tbody>
    </table>
  )
}

const DeliveryTableRow = ({ row, currency, head }) => {
  const CellComponent = head ? 'th' : 'td'

  return (
    <tr>
      {row.map((cell, cellIndex) => {
        const { hidden, bold, color } = cell
        const className = classNames('a-break-long-words', {
          'u-hidden': hidden,
          'u-font-weight--bold': bold,
          [`u-color--${color}`]: color,
        })

        return (
          <CellComponent key={`delivery-cost-cell-${cellIndex}`}>
            <span className={className}>
              {cell?.text}
              {cell?.logo && cell?.alt && (
                <Image
                  src={cell.logo}
                  alt={cell.alt}
                  width={100}
                  height={40}
                  className="a-fullwidth-image u-padding--left u-padding--right"
                />
              )}
              {typeof cell?.price !== 'undefined' && (
                <Price amount={cell.price} currency={currency} symbolRight />
              )}
            </span>
          </CellComponent>
        )
      })}
    </tr>
  )
}

DeliveryTableRow.propTypes = {
  row: arrayOf(
    shape({
      text: string,
      logo: shape({}),
      alt: string,
      price: number,
    })
  ).isRequired,
  currency: string.isRequired,
  head: bool,
}

DeliveryTableRow.defaultProps = { head: false }

const DeliveryCosts = ({
  currency,
  shippingData,
  publicRuntimeConfig: {
    publicConfig: { deliveryCosts },
  },
}) => {
  const { showFreeDeliveryText } = deliveryCosts

  return (
    <>
      <WrapIfNeeded
        element="div"
        className={showFreeDeliveryText ? 'u-margin-large--bottom' : undefined}
      >
        <DisplayShippingData
          shippingData={shippingData}
          deliveryCosts={deliveryCosts}
          currency={currency}
        />
      </WrapIfNeeded>
      {showFreeDeliveryText && (
        <div className="a-box u-padding-large--bottom u-padding-large--top u-color--positive u-background-color--rxgreen-extraVariation a-box--bordered u-border-color--positive">
          <div className="u-margin-small--bottom">
            <TextWithIcon icon={<IconSendEnvelope />}>
              <span className="u-margin-small--left">
                <Trans id="buyBox.delivery.freeDelivery.description" />
              </span>
            </TextWithIcon>
          </div>
          <TransWithHtml
            id="buyBox.delivery.freeDelivery.ourTip"
            element="div"
          />
        </div>
      )}
    </>
  )
}

DeliveryCosts.propTypes = {
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  currency: string,
  shippingData: shape({ carriers: arrayOf(shape({})) }),
}

DeliveryCosts.defaultProps = {
  currency: 'EUR',
  shippingData: { carriers: [] },
}

export default withShopAndRuntimeConfig(DeliveryCosts)
